import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from "react-router-dom";
import { format, addDays } from "date-fns";
import toast from 'react-hot-toast';
import { saveAs } from "file-saver";
import {
  useTheme, 
  useMediaQuery,
  Box, 
  Typography, 
  CircularProgress, 
  Chip, 
  Tooltip,
  Select, 
  MenuItem, 
} from '@mui/material';
import {
  Download, 
  IosShare, 
  Update, 
  AddCircleOutline,
} from '@mui/icons-material';
import useAuthCheck from 'hooks/useAuthCheck';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
import { sanitizeData } from 'state/helpers';
import { exportToExcel } from 'state/excel';
import { logClientAction } from 'state/logger';
import { buildPermissionMap, hasRight } from 'state/checkPermission';
import ScrollToTop from 'components/ScrollToTop';
import Header from 'components/Header';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import StyledButton from 'components/StyledButton';
import { boxStyles as styles } from 'components/CustomStyles';
import DataGridComponent from 'components/DataGridComponent';
import RequestDetail from './RequestDetail';
import RequestCard from './RequestCard';


/* ─── constants ─── */
const STATUS_COLORS = {
  Approved: 'success',
  Rejected: 'error',
  Pending:  'warning',
};


/* ─── Main page ─── */
const CrewRequests = () => {
  useAuthCheck();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user);

  const permissionMap = useMemo(
    () => buildPermissionMap(user?.permissions),
    [user?.permissions]
  );
  const canCreate = hasRight(permissionMap, 'CrewRequests', 'create', false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const { startDate, endDate } = useDateRangeContext();
  
  const scrollRef = useRef(null);

  const [loading, setLoading]     = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError]         = useState(null);
  const [requests, setRequests]   = useState([]);
  const [statusFilter, setStatusFilter] = useState('All');
  const [selected, setSelected] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({ _id: false });


  const fetchRequests = useCallback(async (silent = false) => {
    if (!startDate || !endDate) return;
    try {
      setError(null);
      if (silent) setIsFetching(true);
      else setLoading(true);
      const response = await api.post('/crewrequests', { startDate, endDate });
      const data = response.data?.data || [];
      setRequests(data);
      setSelected((prev) => prev ? (data.find((r) => r._id === prev._id) ?? null) : null);
    } catch (err) {
      setError(err);
      toast.error(err?.message || "Failed to fetch requests!");
    } finally {
      if (silent) setIsFetching(false);
      else setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const filteredRequests = useMemo(() => {
    if (!Array.isArray(requests)) return [];
    if (statusFilter === 'All') return requests;
    return requests.filter((r) => r.status?.status === statusFilter);
  }, [requests, statusFilter]);

  const handleRowDoubleClick = (params) => {
    setSelected(params.row);
    setDialogOpen(true);
  };

  const handleUpdateSuccess = (deleted = false) => {
    if (deleted) setDialogOpen(false);
    fetchRequests(true);
  };

  const handleExport = useCallback(async () => {
    try {
      if (!filteredRequests.length) return;
      let output = "";
      for (const req of filteredRequests) {
        if (!req.crewCode) continue;
        const code = req.requestType || "";
        for (let d = new Date(req.startDate); d <= new Date(req.endDate); d = addDays(d, 1)) {
          output += `${req.crewCode},${format(d, "ddMMyyyy")},${code}\n`;
        }
      }
      if (!output) { toast.error("No data to export!"); return; }
      const ts = new Date().toISOString().replace(/[-T:.Z]/g, '').slice(0, 14);
      saveAs(new Blob([output], { type: "text/plain;charset=utf-8" }), `Crew_Request_Job5_${ts}.txt`);
      toast.success("Exported successfully.");
    } catch (err) {
      toast.error(err?.message || "Export failed!");
    }
  }, [filteredRequests]);

  const counts = useMemo(() => {
    const c = { Pending: 0, Approved: 0, Rejected: 0 };
    requests.forEach((r) => { if (c[r.status?.status] !== undefined) c[r.status.status]++; });
    return c;
  }, [requests]);

  const columns = useMemo(() => [
    {
      field: 'no',
      headerName: 'No',
      width: 60,
      filterable: false,
      sortable: false,
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
    },
    { field: 'crewCode',    headerName: 'CrewCode',  flex: 1 },
    {
      field: 'crewName',
      headerName: 'CrewName',
      flex: 2.5,
      renderCell: (params) => (
        <Typography
          variant="body2"
          onClick={(e) => { 
            e.stopPropagation(); 
            setSelected(params.row); 
            setDialogOpen(true); 
          }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            height: '100%',
            cursor: 'pointer',
            '&:hover': { textDecoration: 'underline' }
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    { field: 'requestType', headerName: 'Type',      flex: 1 },
    { field: 'title',       headerName: 'Title',     flex: 3 },
    {
      field: 'startDate',
      headerName: 'StartDate',
      flex: 1.2,
      valueFormatter: (value) => value ? format(new Date(value), 'dd/MM/yyyy') : '',
    },
    {
      field: 'endDate',
      headerName: 'EndDate',
      flex: 1.2,
      valueFormatter: (value) => value ? format(new Date(value), 'dd/MM/yyyy') : '',
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1.2,
      valueGetter: (value) => value?.status ?? 'Pending',
      renderCell: (params) => (
        <Chip 
          size="small" 
          label={params.value} 
          color={STATUS_COLORS[params.value] ?? 'default'} 
        />
      ),
    },
    {
      field: 'comments',
      headerName: 'Comments',
      width: 100,
      valueGetter: (value) => Array.isArray(value) ? value.length : 0,
    },
  ], []);

  
  return (
    <Box className="main-container-box">
      {/* ── Toolbar ── */}
      <FlexBetween>
        <Header
          title="Crew Requests"
          subtitle={loading ? 'Loading...' : `List of Requests`}
        />
        <Box display="flex" alignItems="center" gap={1}>
          <Select
            size="small"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            sx={{  width: { xs: '6.8rem', sm: '8rem' } }}
          >
            <MenuItem value="All">All ({requests.length})</MenuItem>
            <MenuItem value="Pending">Pending ({counts.Pending})</MenuItem>
            <MenuItem value="Approved">Approved ({counts.Approved})</MenuItem>
            <MenuItem value="Rejected">Rejected ({counts.Rejected})</MenuItem>
          </Select>

          <Tooltip title="Download Excel" arrow>
            <span>
              <StyledButton onClick={() => {
                exportToExcel(sanitizeData(filteredRequests, ['_id', '__v', 'createdAt', 'updatedAt', 'deletedAt']), "CrewRequests", []);
                logClientAction("EXPORT_REPORT", { fileName: "CrewRequests", format: "Excel", startDate, endDate });
              }}>
                <Download />
              </StyledButton>
            </span>
          </Tooltip>

          <Tooltip title="Export Job #5 (.txt)" arrow>
            <span>
              <StyledButton onClick={() => {
                if (!hasRight(permissionMap, 'CrewRequests', 'export')) return;
                handleExport();
              }}>
                <IosShare />
              </StyledButton>
            </span>
          </Tooltip>

          <Tooltip title="Refresh" arrow>
            <span>
              <StyledButton onClick={fetchRequests}>
                <Update />
              </StyledButton>
            </span>
          </Tooltip>

          {canCreate && (
            <Tooltip title="New Request" arrow>
              <span>
                <StyledButton onClick={() => {
                  if (!hasRight(permissionMap, 'CrewRequests', 'create')) return;
                  navigate("/crewrequests/new");
                }}>
                  <AddCircleOutline />
                </StyledButton>
              </span>
            </Tooltip>
          )}
        </Box>
      </FlexBetween>

      {/* ── Body ── */}
      {loading ? (
        <FlexCenter sx={{ ...styles }}>
          <CircularProgress />
        </FlexCenter>
      ) : error ? (
        <FlexCenter sx={{ ...styles }}>
          <Typography color="error">{error?.message}</Typography>
        </FlexCenter>
      ) : (
        <Box
          display="grid"
          gridTemplateColumns="repeat(12, minmax(0, 1fr))"
          gap={2}
        >          
          <Box
            ref={scrollRef}
            height={LAYOUT_HEIGHT}
            sx={{ gridColumn: 'span 12', overflowY: 'auto' }}
          >
            {isMobile ? (              
              filteredRequests.length === 0 ? (
                <FlexCenter sx={{ ...styles }}>
                  <Typography color="text.secondary">No requests found.</Typography>
                </FlexCenter>
              ) : (
                <Box  
                  display="flex"
                  flexDirection="column"
                  gap={2}
                >
                  {filteredRequests?.map((req) => (
                    <RequestCard
                      key={req._id}
                      request={req}
                      onClick={() => { setSelected(req); setDialogOpen(true); }}
                    />
                  ))}
                </Box>
              )              
            ) : (
              <DataGridComponent
                rows={filteredRequests}
                columns={columns}
                columnVisibilityModel={columnVisibilityModel}
                setColumnVisibilityModel={setColumnVisibilityModel}
                loading={isFetching}
                props={{ onRowDoubleClick: handleRowDoubleClick }}
              />
            )}
            
            <ScrollToTop scrollRef={scrollRef} />
          </Box>
        </Box>
      )}

      <RequestDetail
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        request={selected}
        onUpdateSuccess={handleUpdateSuccess}
      />
    </Box>
  );
};

export default CrewRequests;
